import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, CheckCircle, MessageCircle, ArrowRight, Shield, IndianRupee, Star, PiggyBank } from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' } }),
};

const InvestmentPlansPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Investment Plans in India | SIP, PPF, ELSS & Mutual Funds | AGO Innovations',
    description:
      'Start your investment journey with AGO Innovations. Expert advisory on SIP, PPF, ELSS, mutual funds, and investment plans for every goal. Free consultation for young professionals and students.',
    keywords:
      'investment plans India, SIP investment, PPF investment, ELSS tax saving, investment planning services, mutual fund investment India, best investment plan 2026, investment advisor India',
    canonical: 'https://agoinnovations.in/finance/investment-plans',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: 'Investment Planning Advisory — AGO Innovations',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description: 'Expert investment planning advisory. SIP, PPF, ELSS, and personalised investment strategies.',
      areaServed: 'IN',
      serviceType: 'Investment Planning',
    },
  });

  const plans = [
    { name: 'SIP in Mutual Funds', desc: 'Start with just ₹500/month. Systematic Investment Plans in equity, debt, or index funds. Best for long-term wealth creation.', badge: 'Most Popular', color: 'bg-emerald-700' },
    { name: 'PPF (Public Provident Fund)', desc: 'Government-backed, 7.1% tax-free returns. 15-year lock-in. EEE status — invest, interest, and maturity all tax-free.', badge: 'Safe & Guaranteed', color: 'bg-blue-700' },
    { name: 'ELSS (Tax Saving Funds)', desc: 'Equity mutual funds with 3-year lock-in. Tax deduction up to ₹1.5 lakh under Section 80C. High return potential.', badge: 'Tax Saving', color: 'bg-purple-700' },
    { name: 'Fixed Deposits & RD', desc: 'Low-risk, guaranteed returns. Good for emergency funds and short-term goals. FD rates 6.5–8% in 2026.', badge: 'Low Risk', color: 'bg-orange-600' },
  ];

  const features = [
    { icon: <TrendingUp className="w-5 h-5" />, title: 'Goal-Based Planning', desc: 'We match investments to your goals — home, education, retirement, or wealth creation.' },
    { icon: <IndianRupee className="w-5 h-5" />, title: 'Start with ₹500', desc: 'No minimum barrier. Start a SIP with ₹500/month and increase as your income grows.' },
    { icon: <Shield className="w-5 h-5" />, title: 'Tax Optimisation', desc: 'Maximise Section 80C, 80D, and 10(10D) benefits to reduce your tax outgo.' },
    { icon: <Star className="w-5 h-5" />, title: 'Expert Advisors', desc: 'Personalised advice — not generic tips. Your goals, budget, and risk profile guide the plan.' },
    { icon: <PiggyBank className="w-5 h-5" />, title: 'Emergency Fund First', desc: 'We help you build a solid financial foundation before aggressive investing.' },
    { icon: <CheckCircle className="w-5 h-5" />, title: 'Regular Portfolio Review', desc: 'Annual review and rebalancing to keep your portfolio aligned with your goals.' },
  ];

  const faqs = [
    { q: 'What is the best investment plan for beginners in India?', a: 'SIP in index funds (Nifty 50 or Sensex) is the best starting point. Low cost, market-linked, historically 12–14% annual returns over 10+ years. Start with ₹500–1,000/month.' },
    { q: 'How does a SIP work?', a: 'A Systematic Investment Plan (SIP) auto-deducts a fixed amount monthly from your bank account and invests it in a chosen mutual fund. You benefit from rupee cost averaging and compounding.' },
    { q: 'Which investment gives tax benefits in India?', a: 'PPF, ELSS (up to ₹1.5 lakh under 80C), NPS (additional ₹50,000 under 80CCD), and health insurance premiums (80D) give significant tax deductions.' },
    { q: 'Is it safe to invest in mutual funds in India?', a: 'Mutual funds are regulated by SEBI. All funds are subject to market risks, but diversified equity funds have historically delivered positive returns over 5–10 year periods.' },
    { q: 'Can students invest in mutual funds?', a: 'Yes. Students above 18 can invest in mutual funds through KYC. Starting early with even ₹500/month creates significant wealth through compounding by age 40–45.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-emerald-900 via-teal-900 to-green-900 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative z-10 max-w-4xl mx-auto">
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">📈 Finance — Investment Plans</span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">Investment Plans<span className="block text-yellow-400">in India 2026</span></h1>
          <p className="text-emerald-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">Build wealth systematically with SIP, PPF, ELSS, and expert-guided investment plans. Start with as little as ₹500/month. Free personalised consultation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/contact')} className="bg-yellow-400 hover:bg-yellow-300 text-emerald-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2">Start Investing <ArrowRight className="w-5 h-5" /></motion.button>
            <a href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20investment%20planning%20advice." target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"><MessageCircle className="w-5 h-5" /> WhatsApp Us</a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-emerald-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Start from ₹500/month</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Tax Saving Plans</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Free Personalised Advisory</span>
          </div>
        </motion.div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Best Investment Plans in India</h2>
            <p className="text-gray-500 text-lg">From risk-free PPF to market-linked SIPs — find what suits your goals.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {plans.map((plan, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600"><TrendingUp className="w-6 h-6" /></div>
                  <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${plan.color}`}>{plan.badge}</span>
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{plan.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">How AGO Innovations Helps You Invest</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
                <div className="w-11 h-11 bg-emerald-600 rounded-xl flex items-center justify-center text-white mb-4">{f.icon}</div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Investment Planning FAQs</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-br from-emerald-800 to-teal-900 text-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black mb-4">Start Your Investment Journey Today</h2>
          <p className="text-emerald-200 mb-8">The best time to invest was yesterday. The second best time is now.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/contact')} className="bg-yellow-400 hover:bg-yellow-300 text-emerald-900 font-bold px-8 py-4 rounded-full transition-colors">Book Free Consultation</button>
            <button onClick={() => navigate('/finance/mutual-funds')} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-colors">Explore Mutual Funds</button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default InvestmentPlansPage;
